import { Box, Button, Typography } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { OrderAddItemsListsTable } from '../OrderAddItemsListsTable/OrderAddItemsListsTable';

import type { CustomList, CustomListItem } from 'entities/CustomList';

interface OrderAddItemsListsPanelProps {
    list: CustomList | null;
    items: CustomListItem[];
    itemSearch: string;
    categoryFilter: string;
    isLoading: boolean;
    hasError?: boolean;
    onRetry?: () => void;
}

export const OrderAddItemsListsPanel = memo(
    ({
        list,
        items,
        itemSearch,
        categoryFilter,
        isLoading,
        hasError,
        onRetry,
    }: OrderAddItemsListsPanelProps) => {
        const { t } = useTranslation();

        if (!list) {
            return (
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 0,
                        padding: '16px',
                    }}
                    data-testclass="order-add-items-lists-panel-empty"
                >
                    <Typography variant="body1" color="text.secondary">
                        {t('customLists.selectListToAddItems')}
                    </Typography>
                </Box>
            );
        }

        if (hasError && !isLoading) {
            return (
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '16px',
                    }}
                    data-testclass="order-add-items-lists-detail-error"
                >
                    <Typography variant="body1" color="text.secondary">
                        {t('customLists.failedToLoadListItems')}
                    </Typography>
                    <Button variant="outlined" onClick={onRetry}>
                        {t('customLists.tryAgain')}
                    </Button>
                </Box>
            );
        }

        return (
            <OrderAddItemsListsTable
                items={items}
                itemSearch={itemSearch}
                categoryFilter={categoryFilter}
                listId={list.id}
                isLoading={isLoading}
            />
        );
    },
);

OrderAddItemsListsPanel.displayName = 'OrderAddItemsListsPanel';
